// `OnboardingRedirect` — root-level guard that sends an authenticated user
// who hasn't finished the wizard into `/onboarding`.
//
// Mirrors `<HardBlockRedirect>`. Mounts inside the authed shell so the query
// only fires for logged-in users; un-authed sessions get caught by
// `useAuthGuard()` first. Allowed paths pass through (the wizard route
// itself).

import { useEffect } from 'react';

import { useNavigate, useRouterState } from '@tanstack/react-router';

import { useOnboardingState } from '@barel/api-client/react';

const ALLOWED_PATHS = [/^\/onboarding(?:\/|$)/];

export interface OnboardingRedirectProps {
  /** Override allowed paths (tests). */
  allowedPaths?: RegExp[];
}

export function OnboardingRedirect({ allowedPaths = ALLOWED_PATHS }: OnboardingRedirectProps = {}) {
  const navigate = useNavigate();
  const router = useRouterState();
  const query = useOnboardingState();

  useEffect(() => {
    if (!query.data) return;
    if (query.data.completedAt != null) return;
    const path = router.location.pathname;
    if (allowedPaths.some((re) => re.test(path))) return;
    void navigate({ to: '/onboarding', replace: true });
  }, [query.data, router.location.pathname, navigate, allowedPaths]);

  return null;
}
